/**
 * Predictive Intelligence Engine
 * 
 * Anticipatory AI that acts before being asked:
 * 1. Intention Prediction - Predict next actions from patterns
 * 2. Conflict Detection - Identify scheduling/commitment conflicts
 * 3. Opportunity Identification - Suggest optimizations
 * 4. Proactive Automation - Execute high-confidence predictions
 */

import { blink } from '@/lib/blink';
import { selfLearningMemory } from './selfLearningMemory';
import { contextAwarenessEngine } from './contextAwarenessEngine';
import { safeJsonParse } from '@/lib/utils';



/**
 * Predicted action with confidence score
 */
interface PredictedAction {
  action: string;
  actionType: 'schedule_meeting' | 'send_email' | 'create_task' | 'block_time' | 'delegate' | 'cancel_meeting';
  confidence: number; // 0-1
  parameters: Record<string, any>;
  reasoning: string;
  timeframe: string;
  autoExecute: boolean;
  dependencies: string[];
}

/**
 * Detected conflict
 */
interface DetectedConflict {
  id: string;
  type: 'double_commitment' | 'missing_prerequisite' | 'overcommitment' | 'scheduling_conflict';
  severity: 'critical' | 'high' | 'medium' | 'low';
  details: string;
  affectedItems: string[];
  suggestedResolution: string;
  proactiveAction?: {
    type: string;
    description: string;
    draftContent?: string;
  };
}

/**
 * Identified opportunity
 */
interface IdentifiedOpportunity {
  id: string;
  type: 'consolidate_meetings' | 'optimize_schedule' | 'reconnect' | 'delegate_task' | 'cancel_low_value';
  benefit: string;
  suggestion: string;
  impactScore: number; // 0-100
  implementation: {
    steps: string[];
    estimatedTimeeSavings?: number; // minutes
  };
}

export class PredictiveIntelligenceEngine {
  private userId: string | null = null;
  private predictionCache: Map<string, PredictedAction> = new Map();

  async initialize(userId: string): Promise<void> {
    this.userId = userId;
    await selfLearningMemory.initialize(userId);
    await contextAwarenessEngine.initialize(userId);
  }

  /**
   * Predict user's next intended action based on current context
   */
  async predictNextAction(
    recentActions: Array<{ type: string; timestamp: Date; context: any }>,
    currentContext: any
  ): Promise<PredictedAction[]> {
    if (!this.userId) throw new Error('Predictive engine not initialized');

    // Get historical patterns from procedural memory
    const procedures = await blink.db.searchIndex.list({
      where: {
        userId: this.userId,
        itemType: 'procedural_memory'
      },
      limit: 20
    });

    // Build context for AI prediction
    const actionSequence = recentActions.map(a => `${a.type} at ${a.timestamp.toISOString()}`).join(', ');
    
    // Use AI to predict next actions
    const { object } = await blink.ai.generateObject({
      prompt: `Based on this user's recent actions and current context, predict their next 1-3 intended actions:

Recent Actions: ${actionSequence}

Current Context:
- Time: ${new Date().toISOString()}
- Day: ${new Date().toLocaleDateString('en-US', { weekday: 'long' })}
- Context: ${JSON.stringify(currentContext)}

Historical Patterns:
${procedures.map(p => {
  const proc = typeof p.content === 'string' ? safeJsonParse(p.content) : p.content;
  return `- When ${proc.pattern?.trigger}, user typically: ${proc.pattern?.actions?.map((a: any) => a.type).join(', ')}`;
}).join('\n')}

For each predicted action, provide:
1. Action type (schedule_meeting, send_email, create_task, block_time, delegate, cancel_meeting)
2. Confidence (0-1)
3. Parameters (who, what, when, etc.)
4. Reasoning
5. Timeframe (next_hour, today, tomorrow, this_week)
6. Whether to auto-execute (only if confidence > 0.85)`,
      schema: {
        type: 'object',
        properties: {
          predictions: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                action: { type: 'string' },
                actionType: { type: 'string', enum: ['schedule_meeting', 'send_email', 'create_task', 'block_time', 'delegate', 'cancel_meeting'] },
                confidence: { type: 'number' },
                parameters: { type: 'object' },
                reasoning: { type: 'string' },
                timeframe: { type: 'string' },
                autoExecute: { type: 'boolean' },
                dependencies: { type: 'array', items: { type: 'string' } }
              }
            }
          }
        },
        required: ['predictions']
      }
    });

    const result = object as any;
    return result.predictions || [];
  }

  /**
   * Detect conflicts in schedule and commitments
   */
  async detectConflicts(): Promise<DetectedConflict[]> {
    if (!this.userId) throw new Error('Predictive engine not initialized');

    const conflicts: DetectedConflict[] = [];

    // Get upcoming calendar events
    const now = new Date();
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    const events = await blink.db.calendarEvents.list({
      where: { userId: this.userId },
      limit: 100
    });

    // Get active tasks
    const tasks = await blink.db.tasks.list({
      where: {
        userId: this.userId,
        status: 'pending'
      },
      limit: 50
    });

    // Detect double commitments
    const eventsByTime = new Map<string, typeof events>();
    events.forEach(event => {
      const key = new Date(event.startTime).toISOString().slice(0, 16); // Hour precision
      if (!eventsByTime.has(key)) {
        eventsByTime.set(key, []);
      }
      eventsByTime.get(key)!.push(event);
    });

    eventsByTime.forEach((overlappingEvents, timeKey) => {
      if (overlappingEvents.length > 1) {
        conflicts.push({
          id: `conflict_${Date.now()}_${Math.random().toString(36).substring(7)}`,
          type: 'scheduling_conflict',
          severity: 'high',
          details: `${overlappingEvents.length} events scheduled at ${new Date(timeKey).toLocaleString()}`,
          affectedItems: overlappingEvents.map(e => e.id),
          suggestedResolution: `Reschedule ${overlappingEvents.length - 1} of these events`,
          proactiveAction: {
            type: 'draft_reschedule_email',
            description: 'Draft polite reschedule request for conflicting meetings'
          }
        });
      }
    });

    // Detect missing prerequisites (task due but dependencies not met)
    for (const task of tasks) {
      if (task.dueDate) {
        const dueDate = new Date(task.dueDate);
        const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
        
        if (hoursUntilDue < 48 && hoursUntilDue > 0) {
          // Check if user has enough free time to complete
          const taskEvents = events.filter(e => {
            const eventTime = new Date(e.startTime);
            return eventTime >= now && eventTime <= dueDate;
          });

          const busyHours = taskEvents.reduce((sum, e) => {
            const start = new Date(e.startTime);
            const end = new Date(e.endTime);
            return sum + (end.getTime() - start.getTime()) / (1000 * 60 * 60);
          }, 0);

          const availableHours = hoursUntilDue - busyHours;
          const estimatedHoursNeeded = 8; // Default estimate

          if (availableHours < estimatedHoursNeeded) {
            conflicts.push({
              id: `conflict_${Date.now()}_${Math.random().toString(36).substring(7)}`,
              type: 'double_commitment',
              severity: 'high',
              details: `Task "${task.title}" due ${dueDate.toLocaleDateString()} but only ${availableHours.toFixed(1)} hours available (need ~${estimatedHoursNeeded})`,
              affectedItems: [task.id, ...taskEvents.map(e => e.id)],
              suggestedResolution: `Either reschedule ${Math.ceil((estimatedHoursNeeded - availableHours) / 2)} meetings or negotiate deadline extension`,
              proactiveAction: {
                type: 'draft_deadline_extension',
                description: 'Draft diplomatic deadline extension email',
                draftContent: `I wanted to update you on the progress of "${task.title}". Due to several high-priority commitments this week, I'll need until [DATE] to deliver this at the quality level expected. Would this timeline work for you?`
              }
            });
          }
        }
      }
    }

    // Detect overcommitment (too many meetings in one day)
    const eventsByDay = new Map<string, typeof events>();
    events.forEach(event => {
      const dayKey = new Date(event.startTime).toISOString().slice(0, 10);
      if (!eventsByDay.has(dayKey)) {
        eventsByDay.set(dayKey, []);
      }
      eventsByDay.get(dayKey)!.push(event);
    });

    eventsByDay.forEach((dayEvents, dayKey) => {
      const meetingCount = dayEvents.filter(e => e.eventType === 'meeting').length;
      const totalHours = dayEvents.reduce((sum, e) => {
        const start = new Date(e.startTime);
        const end = new Date(e.endTime);
        return sum + (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      }, 0);

      if (meetingCount > 6 || totalHours > 7) {
        conflicts.push({
          id: `conflict_${Date.now()}_${Math.random().toString(36).substring(7)}`,
          type: 'overcommitment',
          severity: 'medium',
          details: `${meetingCount} meetings (${totalHours.toFixed(1)} hours) on ${new Date(dayKey).toLocaleDateString()}`,
          affectedItems: dayEvents.map(e => e.id),
          suggestedResolution: 'Consider declining some non-critical meetings or rescheduling to spread the load',
          proactiveAction: {
            type: 'identify_skippable_meetings',
            description: 'Analyze which meetings could be declined or made async'
          }
        });
      }
    });

    return conflicts;
  }

  /**
   * Identify optimization opportunities
   */
  async identifyOpportunities(): Promise<IdentifiedOpportunity[]> {
    if (!this.userId) throw new Error('Predictive engine not initialized');

    const opportunities: IdentifiedOpportunity[] = [];

    // Get calendar data
    const now = new Date();
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    const events = await blink.db.calendarEvents.list({
      where: { userId: this.userId },
      limit: 100
    });

    const upcomingEvents = events.filter(e => {
      const eventTime = new Date(e.startTime);
      return eventTime >= now && eventTime <= nextWeek;
    });

    // Opportunity 1: Consolidate meetings with same people
    const meetingsByContact = new Map<string, typeof events>();
    upcomingEvents.forEach(event => {
      if (event.attendees) {
        const attendeeList = typeof event.attendees === 'string' 
          ? safeJsonParse(event.attendees) 
          : event.attendees;
        
        attendeeList.forEach((attendee: string) => {
          if (!meetingsByContact.has(attendee)) {
            meetingsByContact.set(attendee, []);
          }
          meetingsByContact.get(attendee)!.push(event);
        });
      }
    });

    meetingsByContact.forEach((contactEvents, contact) => {
      if (contactEvents.length >= 2) {
        const topics = contactEvents.map(e => e.title).join(', ');
        opportunities.push({
          id: `opp_${Date.now()}_${Math.random().toString(36).substring(7)}`,
          type: 'consolidate_meetings',
          benefit: `Consolidate ${contactEvents.length} separate meetings into one`,
          suggestion: `${contact} is in ${contactEvents.length} separate meetings this week (${topics}). Consider combining into one comprehensive meeting.`,
          impactScore: contactEvents.length * 15, // 15 points per consolidated meeting
          implementation: {
            steps: [
              'Review topics for all meetings',
              'Send meeting invite for combined topics',
              'Cancel individual meetings with explanation'
            ],
            estimatedTimeeSavings: (contactEvents.length - 1) * 30 // Save 30 min per cancelled meeting
          }
        });
      }
    });

    // Opportunity 2: Recurring meetings with low attendance
    const recurringMeetings = upcomingEvents.filter(e => 
      e.title && (e.title.toLowerCase().includes('standup') || 
                  e.title.toLowerCase().includes('recurring') ||
                  e.title.toLowerCase().includes('weekly'))
    );

    if (recurringMeetings.length > 0) {
      opportunities.push({
        id: `opp_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        type: 'cancel_low_value',
        benefit: 'Eliminate or restructure low-value recurring meetings',
        suggestion: `You have ${recurringMeetings.length} recurring meetings. Consider which could be async updates or less frequent.`,
        impactScore: recurringMeetings.length * 10,
        implementation: {
          steps: [
            'Review attendance and engagement for each recurring meeting',
            'Propose moving low-engagement meetings to async (Slack/email)',
            'Reduce frequency of meetings that could be biweekly instead of weekly'
          ],
          estimatedTimeeSavings: recurringMeetings.length * 20 // 20 min per optimized meeting
        }
      });
    }

    // Opportunity 3: Re-engage with key contacts
    const contacts = await blink.db.contacts.list({
      where: { userId: this.userId },
      limit: 50
    });

    const importantContacts = contacts.filter(c => 
      c.relationshipType?.includes('client') || 
      c.relationshipType?.includes('key') ||
      c.relationshipHealth === 'excellent'
    );

    for (const contact of importantContacts) {
      if (contact.lastInteractionAt) {
        const daysSinceInteraction = (now.getTime() - new Date(contact.lastInteractionAt).getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysSinceInteraction > 42) { // 6 weeks
          opportunities.push({
            id: `opp_${Date.now()}_${Math.random().toString(36).substring(7)}`,
            type: 'reconnect',
            benefit: 'Maintain relationship with key contact',
            suggestion: `Haven't connected with ${contact.name || contact.email} in ${Math.floor(daysSinceInteraction)} days. Consider scheduling a check-in.`,
            impactScore: 25,
            implementation: {
              steps: [
                'Draft friendly check-in email',
                'Suggest coffee or quick call',
                'Ask about their current projects'
              ]
            }
          });
        }
      }
    }

    // Sort by impact score
    return opportunities.sort((a, b) => b.impactScore - a.impactScore);
  }

  /**
   * Execute high-confidence predictions automatically
   */
  async executeHighConfidencePredictions(
    predictions: PredictedAction[]
  ): Promise<Array<{ prediction: PredictedAction; executed: boolean; result?: any; error?: string }>> {
    if (!this.userId) throw new Error('Predictive engine not initialized');

    const results: Array<{ prediction: PredictedAction; executed: boolean; result?: any; error?: string }> = [];

    for (const prediction of predictions) {
      if (prediction.autoExecute && prediction.confidence >= 0.85) {
        try {
          // Execute based on action type
          let result: any;
          
          switch (prediction.actionType) {
            case 'schedule_meeting':
              result = await this.scheduleProactiveMeeting(prediction);
              break;
            case 'send_email':
              result = await this.sendProactiveEmail(prediction);
              break;
            case 'create_task':
              result = await this.createProactiveTask(prediction);
              break;
            case 'block_time':
              result = await this.blockProactiveTime(prediction);
              break;
            default:
              throw new Error(`Unsupported auto-execute action: ${prediction.actionType}`);
          }

          results.push({
            prediction,
            executed: true,
            result
          });

          // Log success for learning
          await selfLearningMemory.processFeedback(
            prediction.action,
            'approved'
          );

        } catch (error) {
          results.push({
            prediction,
            executed: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          });

          // Log failure for learning
          await selfLearningMemory.processFeedback(
            prediction.action,
            'rejected'
          );
        }
      } else {
        // Queue for user approval
        results.push({
          prediction,
          executed: false,
          result: 'Queued for user approval (confidence < 0.85 or auto-execute disabled)'
        });
      }
    }

    return results;
  }

  /**
   * Generate proactive briefing for user
   */
  async generateProactiveBriefing(): Promise<string> {
    if (!this.userId) throw new Error('Predictive engine not initialized');

    const now = new Date();
    
    // Get predictions, conflicts, and opportunities
    const [predictions, conflicts, opportunities] = await Promise.all([
      this.predictNextAction([], { currentTime: now }),
      this.detectConflicts(),
      this.identifyOpportunities()
    ]);

    // Get today's events
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayEvents = await blink.db.calendarEvents.list({
      where: { userId: this.userId },
      limit: 20
    });

    const todayMeetings = todayEvents.filter(e => {
      const eventDate = new Date(e.startTime);
      return eventDate >= today && eventDate < tomorrow;
    });

    // Generate briefing
    let briefing = `## Morning Brief (${now.toLocaleTimeString()})\n\n`;
    
    // Your Day Ahead
    briefing += `### 📅 Your Day Ahead:\n`;
    todayMeetings.forEach(meeting => {
      const time = new Date(meeting.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      briefing += `• ${time} - ${meeting.title}`;
      if (meeting.location) briefing += ` (${meeting.location})`;
      briefing += `\n`;
    });
    briefing += `\n`;

    // Conflicts and Issues
    if (conflicts.length > 0) {
      briefing += `### ⚠️ Attention Required:\n`;
      conflicts.slice(0, 3).forEach(conflict => {
        briefing += `• **${conflict.severity.toUpperCase()}**: ${conflict.details}\n`;
        briefing += `  Suggestion: ${conflict.suggestedResolution}\n`;
      });
      briefing += `\n`;
    }

    // Predictions
    if (predictions.length > 0) {
      briefing += `### 🔮 Predicted Actions:\n`;
      predictions.slice(0, 3).forEach(pred => {
        briefing += `• ${pred.action} (${(pred.confidence * 100).toFixed(0)}% confidence)\n`;
        briefing += `  ${pred.reasoning}\n`;
      });
      briefing += `\n`;
    }

    // Opportunities
    if (opportunities.length > 0) {
      briefing += `### 💡 Optimization Opportunities:\n`;
      opportunities.slice(0, 3).forEach(opp => {
        briefing += `• ${opp.suggestion}\n`;
        if (opp.implementation.estimatedTimeeSavings) {
          briefing += `  Time savings: ~${opp.implementation.estimatedTimeeSavings} minutes\n`;
        }
      });
    }

    return briefing;
  }

  // Private helper methods

  private async scheduleProactiveMeeting(prediction: PredictedAction): Promise<any> {
    // Would integrate with calendar service to create meeting
    return { success: true, meetingId: `meeting_${Date.now()}` };
  }

  private async sendProactiveEmail(prediction: PredictedAction): Promise<any> {
    // Would integrate with email service to send email
    return { success: true, emailId: `email_${Date.now()}` };
  }

  private async createProactiveTask(prediction: PredictedAction): Promise<any> {
    if (!this.userId) throw new Error('Not initialized');
    
    const task = await blink.db.tasks.create({
      id: `task_${Date.now()}`,
      userId: this.userId,
      title: prediction.parameters.title || prediction.action,
      description: prediction.reasoning,
      priority: 'medium',
      status: 'pending',
      assignedBy: 'ai',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    return { success: true, taskId: task.id };
  }

  private async blockProactiveTime(prediction: PredictedAction): Promise<any> {
    // Would integrate with calendar service to block time
    return { success: true, blockId: `block_${Date.now()}` };
  }
}

export const predictiveIntelligence = new PredictiveIntelligenceEngine();
